import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    FaAddressBook, FaPlus, FaStar, FaArchive, FaPen, FaLock, FaWhatsapp, FaBirthdayCake, FaUsers, FaLayerGroup,
    FaBriefcase, FaHome, FaHandshake, FaTruck, FaUserFriends, FaHeartbeat, FaTools, FaEllipsisH,
    FaGraduationCap, FaBalanceScale, FaMoneyBillWave, FaDumbbell, FaMusic, FaLaptop, FaFirstAid, FaPlane, FaStore, FaChartBar, FaSlidersH,
} from "react-icons/fa";
import { CONTACTS_CONSTANTS, CONTACT_GROUP_CONSTANTS } from "../_MODULES/_API";
import { ContactFormModal } from "../_UI/ContactFormModal";
import { ContactGroupModal } from "../_UI/ContactGroupModal";
import { ContactGroupWorkspaceModal } from "../_UI/ContactGroupWorkspaceModal";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { EmptyState } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { onApiAction } from "../../../../_UI/onApiAction";
import "../_UI/_Styles/Contacts.css";

const groupIcon = (value) => ({
    USERS: <FaUsers />,
    WORK: <FaBriefcase />,
    FAMILY: <FaHome />,
    CLIENTS: <FaHandshake />,
    PROVIDERS: <FaTruck />,
    FRIENDS: <FaUserFriends />,
    HEALTH: <FaHeartbeat />,
    SERVICES: <FaTools />,
    EDUCATION: <FaGraduationCap />,
    LEGAL: <FaBalanceScale />,
    FINANCE: <FaMoneyBillWave />,
    SPORT: <FaDumbbell />,
    MUSIC: <FaMusic />,
    TECH: <FaLaptop />,
    EMERGENCY: <FaFirstAid />,
    TRAVEL: <FaPlane />,
    BUSINESS: <FaStore />,
    OTHER: <FaEllipsisH />,
}[String(value || "USERS").toUpperCase()] || <FaLayerGroup />);

const SEARCH_SKIP_KEYS = new Set(["photoDataUrl"]);

const flattenSearchValues = (value, key = "") => {
    if (SEARCH_SKIP_KEYS.has(key) || value === null || value === undefined) return [];
    if (["string", "number", "boolean"].includes(typeof value)) return [String(value)];
    if (Array.isArray(value)) return value.flatMap((item) => flattenSearchValues(item));
    if (typeof value === "object") return Object.entries(value).flatMap(([childKey, childValue]) => flattenSearchValues(childValue, childKey));
    return [];
};

const contactSearchParts = (contact) => flattenSearchValues(contact);
const groupSearchParts = (group) => [group?.name, group?.description, group?.icon, group?.color];
const CONTACT_TYPE_OPTIONS = [
    { value: "ALL", label: "Todos" },
    { value: "PERSONAL", label: "Personal" }, { value: "WORK", label: "Trabajo / compañero" },
    { value: "FAMILY", label: "Familia" }, { value: "FRIEND", label: "Amigo / conocido" },
    { value: "CLIENT", label: "Cliente" }, { value: "LEAD", label: "Prospecto" },
    { value: "SUPPLIER", label: "Proveedor" }, { value: "PARTNER", label: "Socio / partner" },
    { value: "PROFESSIONAL", label: "Profesional" }, { value: "HEALTH", label: "Salud / médico" },
    { value: "EDUCATION", label: "Educación / docente" }, { value: "SERVICE", label: "Servicio / comercio" },
    { value: "GOVERNMENT", label: "Organismo / trámite" }, { value: "EMERGENCY", label: "Emergencia" },
    { value: "OTHER", label: "Otro" },
];


export const ContactsViewMain = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [useSearch, setSearch] = useState("");
    const [useFormOpen, setFormOpen] = useState(false);
    const [useEditing, setEditing] = useState(null);
    const [defaultGroupId, setDefaultGroupId] = useState("");
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [workspaceScope, setWorkspaceScope] = useState(null);
    const [workspaceSearch, setWorkspaceSearch] = useState("");
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ groupId: "ALL", contactType: "ALL", favorite: "ALL", organization: "", tag: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const dialogClosingRef = useRef(false);

    const CONTACT_QUERY = useApiQuery({ minTimeQuery: 250, enablePagination: false, startLoading: true });
    const GROUP_QUERY = useApiQuery({ minTimeQuery: 220, enablePagination: false, startLoading: true });
    const { actionMutate } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 200 });

    useEffect(() => {
        CONTACT_QUERY.apiGet({ endpoint: CONTACTS_CONSTANTS.LIST });
        GROUP_QUERY.apiGet({ endpoint: CONTACT_GROUP_CONSTANTS.LIST });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = Array.isArray(CONTACT_QUERY.useApiData) ? CONTACT_QUERY.useApiData : [];
    const groups = Array.isArray(GROUP_QUERY.useApiData) ? GROUP_QUERY.useApiData : [];
    const groupMap = useMemo(() => new Map(groups.map((group) => [String(group.id), group])), [groups]);

    useEffect(() => {
        const dialog = searchParams.get("dialog");
        if (dialogClosingRef.current) {
            if (!dialog) dialogClosingRef.current = false;
            return;
        }
        if (!dialog) return;
        if (dialog === "contact-new") {
            setEditing(null);
            const routedGroup = searchParams.get("dialogGroup") || "";
            setDefaultGroupId(routedGroup !== "ALL" && routedGroup !== "NONE" ? routedGroup : "");
            setFormOpen(true);
            return;
        }
        if (dialog.startsWith("contact:")) {
            const contact = rows.find((item) => String(item.id) === dialog.slice(8));
            if (contact) {
                setEditing(contact);
                setDefaultGroupId(contact.groupId ? String(contact.groupId) : "");
                setFormOpen(true);
            }
            return;
        }
        if (dialog === "group-new") {
            setEditingGroup(null); setGroupModalOpen(true); return;
        }
        if (dialog.startsWith("group:")) {
            const group = groups.find((item) => String(item.id) === dialog.slice(6));
            if (group) { setEditingGroup(group); setGroupModalOpen(true); }
        }
    }, [rows, groups, searchParams]);

    const setDialogRoute = (value = null, dialogGroup = null) => {
        const next = new URLSearchParams(window.location.search);
        if (value) next.set("dialog", value); else next.delete("dialog");
        if (dialogGroup) next.set("dialogGroup", String(dialogGroup)); else next.delete("dialogGroup");
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        const routedGroup = searchParams.get("group");
        if (routedGroup === null) {
            setWorkspaceScope(null);
            setWorkspaceSearch("");
            return;
        }
        if (routedGroup !== "ALL" && routedGroup !== "NONE" && !groups.some((group) => String(group.id) === String(routedGroup))) return;
        setWorkspaceScope((current) => String(current ?? "") === String(routedGroup) ? current : String(routedGroup));
    }, [groups, searchParams]);
    const advancedCount = [
        advanced.groupId !== "ALL", advanced.contactType !== "ALL", advanced.favorite !== "ALL",
        String(advanced.organization || "").trim(), String(advanced.tag || "").trim(),
        advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact,
    ].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ groupId: "ALL", contactType: "ALL", favorite: "ALL", organization: "", tag: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const hasSearch = !!String(useSearch || "").trim();
    const hasAdvanced = advancedCount > 0;

    const advancedContacts = useMemo(() => rows.filter((contact) => {
        if (advanced.groupId !== "ALL") {
            if (advanced.groupId === "NONE" ? !!contact.groupId : String(contact.groupId) !== String(advanced.groupId)) return false;
        }
        if (advanced.contactType !== "ALL" && contact.contactType !== advanced.contactType) return false;
        if (advanced.favorite !== "ALL" && (!!contact.isFavorite !== (advanced.favorite === "YES"))) return false;
        if (String(advanced.organization || "").trim() && !onMatchesSearch(advanced.organization, [contact.organization, contact.jobTitle, contact.department])) return false;
        if (String(advanced.tag || "").trim() && !onMatchesSearch(advanced.tag, contact.tags || [])) return false;
        const dateValue = advanced.dateField === "CREATED" ? contact.createdAt : advanced.dateField === "BIRTHDAY" ? contact.birthday : contact.updatedAt;
        return onDateMatches(dateValue, advanced.dateRange, advanced.dateExact);
    }), [rows, advanced]);

    const matchingContacts = useMemo(() => advancedContacts.filter((contact) => onMatchesSearch(useSearch, contactSearchParts(contact))), [advancedContacts, useSearch]);
    const matchingContactIds = useMemo(() => new Set(matchingContacts.map((contact) => String(contact.id))), [matchingContacts]);
    const allUngroupedRows = useMemo(() => rows.filter((contact) => !contact.groupId), [rows]);
    const filteredUngroupedRows = useMemo(() => advancedContacts.filter((contact) => !contact.groupId), [advancedContacts]);
    const ungroupedCount = allUngroupedRows.length;
    const displayUngroupedCount = filteredUngroupedRows.length;
    const matchingUngroupedCount = matchingContacts.filter((contact) => !contact.groupId).length;

    const visibleGroups = useMemo(() => groups.filter((group) => {
        if (advanced.groupId === "NONE") return false;
        if (advanced.groupId !== "ALL" && String(group.id) !== String(advanced.groupId)) return false;
        const groupHasAdvancedContacts = advancedContacts.some((contact) => String(contact.groupId) === String(group.id));
        if (!hasSearch) return !hasAdvanced || advanced.groupId !== "ALL" || groupHasAdvancedContacts;
        if (onMatchesSearch(useSearch, groupSearchParts(group)) && (!hasAdvanced || advanced.groupId !== "ALL" || groupHasAdvancedContacts)) return true;
        return matchingContacts.some((contact) => String(contact.groupId) === String(group.id));
    }), [groups, advanced.groupId, advancedContacts, hasSearch, hasAdvanced, useSearch, matchingContacts]);

    const showUngrouped = displayUngroupedCount > 0 && advanced.groupId !== "NONE"
        ? advanced.groupId === "ALL" && (!hasSearch
            || onMatchesSearch(useSearch, ["Sin grupo", "sin agrupar", "contactos sin grupo"])
            || matchingUngroupedCount > 0)
        : advanced.groupId === "NONE" && displayUngroupedCount > 0 && (!hasSearch || matchingUngroupedCount > 0);
    const groupingBucketCount = groups.length + (ungroupedCount > 0 ? 1 : 0);
    const visibleGroupingBucketCount = visibleGroups.length + (showUngrouped ? 1 : 0);
    const searchableTotal = groups.length + rows.length + (ungroupedCount > 0 ? 1 : 0);
    const searchableMatches = hasSearch || hasAdvanced ? visibleGroups.length + matchingContacts.length + (showUngrouped ? 1 : 0) : searchableTotal;
    const favoriteCount = rows.filter((contact) => contact.isFavorite).length;
    const whatsappCount = rows.filter((contact) => Array.isArray(contact.phones) && contact.phones.some((phone) => phone.whatsapp)).length;
    const birthdayCount = rows.filter((contact) => !!contact.birthday).length;
    const loading = CONTACT_QUERY.useApiLoading || GROUP_QUERY.useApiLoading;

    const workspaceGroup = workspaceScope && workspaceScope !== "ALL" && workspaceScope !== "NONE"
        ? groupMap.get(String(workspaceScope)) || null
        : null;

    const openCreate = (groupId = "") => {
        setEditing(null);
        const normalizedGroup = groupId && groupId !== "ALL" && groupId !== "NONE" ? String(groupId) : "";
        setDefaultGroupId(normalizedGroup);
        setFormOpen(true);
        setDialogRoute("contact-new", normalizedGroup || null);
    };
    const openEdit = (contact) => {
        setEditing(contact);
        setDefaultGroupId(contact?.groupId ? String(contact.groupId) : "");
        setFormOpen(true);
        setDialogRoute(`contact:${contact.id}`);
    };
    const openGroupCreate = () => { setEditingGroup(null); setGroupModalOpen(true); setDialogRoute("group-new"); };
    const openGroupEdit = (group) => { setEditingGroup(group); setGroupModalOpen(true); setDialogRoute(`group:${group.id}`); };
    const openWorkspace = (scope, initialSearch = "") => {
        setWorkspaceSearch(String(initialSearch || ""));
        setWorkspaceScope(String(scope));
        const next = new URLSearchParams(window.location.search);
        next.set("group", String(scope));
        next.delete("contact");
        next.delete("preview");
        next.delete("dialog");
        next.delete("dialogGroup");
        setSearchParams(next, { replace: true });
    };

    const closeWorkspace = () => {
        const next = new URLSearchParams(window.location.search);
        ["group", "contact", "preview", "contactAdvanced", "dialog", "dialogGroup"].forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
        setWorkspaceScope(null);
        setWorkspaceSearch("");
    };

    const onToggleFavorite = async (contact) => {
        const nextValue = !contact.isFavorite;
        CONTACT_QUERY.mutateLocal({ id: contact.id, patch: { isFavorite: nextValue } });
        const response = await actionMutate({
            method: "PATCH", endpoint: CONTACTS_CONSTANTS.UPDATE, id: contact.id,
            data: { isFavorite: nextValue }, successMessage: nextValue ? "Contacto destacado" : "Quitado de destacados",
        });
        if (!response.success) CONTACT_QUERY.mutateLocal({ id: contact.id, patch: { isFavorite: contact.isFavorite } });
    };

    const onArchive = async (contact) => {
        const ok = await confirm.danger("Archivar contacto", `Se archiva "${contact.displayName}". Después podés recuperarlo desde Archivados.`, { confirmText: "Archivar" });
        if (!ok) return;
        CONTACT_QUERY.mutateLocal({ id: contact.id, remove: true });
        const response = await onApiAction({ method: "PATCH", endpoint: CONTACTS_CONSTANTS.ARCHIVE_ONE, pathParams: { id: contact.id }, successMessage: "Contacto archivado" });
        if (!response.success) CONTACT_QUERY.mutateLocal({ insert: contact });
    };

    const onArchiveGroup = async (group) => {
        const count = rows.filter((contact) => String(contact.groupId) === String(group.id)).length;
        const ok = await confirm.danger(
            "Archivar grupo",
            `Se archiva "${group.name}"${count ? ` junto con ${count} ${count === 1 ? "contacto" : "contactos"}` : ""}. Todo se recupera desde Archivados.`,
            { confirmText: "Archivar" },
        );
        if (!ok) return;
        GROUP_QUERY.mutateLocal({ id: group.id, remove: true });
        const removedContacts = rows.filter((contact) => String(contact.groupId) === String(group.id));
        removedContacts.forEach((contact) => CONTACT_QUERY.mutateLocal({ id: contact.id, remove: true }));
        if (String(workspaceScope) === String(group.id)) closeWorkspace();
        const response = await onApiAction({ method: "PATCH", endpoint: CONTACT_GROUP_CONSTANTS.ARCHIVE_ONE, pathParams: { id: group.id }, successMessage: "Grupo archivado" });
        if (!response.success) {
            GROUP_QUERY.mutateLocal({ insert: group });
            removedContacts.forEach((contact) => CONTACT_QUERY.mutateLocal({ insert: contact }));
        }
    };

    const exportRows = matchingContacts.map((contact) => ({
        group: groupMap.get(String(contact.groupId))?.name || "Sin grupo",
        displayName: contact.displayName || "",
        type: contact.contactType || "",
        relationship: contact.relationship || "",
        phone: Array.isArray(contact.phones) ? (contact.phones.find((row) => row?.isPrimary)?.number || contact.phones[0]?.number || "") : "",
        email: Array.isArray(contact.emails) ? (contact.emails.find((row) => row?.isPrimary)?.email || contact.emails[0]?.email || "") : "",
        organization: contact.organization || "",
        jobTitle: contact.jobTitle || "",
        birthday: contact.birthday || "",
        tags: Array.isArray(contact.tags) ? contact.tags.join(", ") : "",
    }));

    return <ViewLayout
        header={<PageHeader title="Contactos"
            description="Agenda privada de contactos organizada por grupos."
            icon={<FaAddressBook />}
            actions={<>
                <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                <AppButton variant="outline" tone="neutral" icon={<FaLayerGroup />} onClick={openGroupCreate}>Nuevo grupo</AppButton>
                <AppButton icon={<FaPlus />} onClick={() => openCreate()}>Nuevo contacto</AppButton>
            </>} />}
        toolbar={(groups.length > 0 || rows.length > 0) ? <ViewToolbar
            filters={<>
                <SearchFilter value={useSearch} onChange={setSearch} total={searchableTotal} matches={searchableMatches}
                    placeholder="Buscar cualquier dato del grupo o contacto..." />
                <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>
                    {advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}
                </AppButton>
            </>}
            expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                <FieldSelect id="contacts_adv_group" label="Grupo" value={advanced.groupId}
                    onChange={(event) => setAdvanced((current) => ({ ...current, groupId: event.target.value }))}
                    options={[{ value: "ALL", label: "Todos" }, { value: "NONE", label: "Sin grupo" }, ...groups.map((group) => ({ value: String(group.id), label: group.name }))]} />
                <FieldSelect id="contacts_adv_type" label="Tipo" value={advanced.contactType}
                    onChange={(event) => setAdvanced((current) => ({ ...current, contactType: event.target.value }))} options={CONTACT_TYPE_OPTIONS} />
                <FieldSelect id="contacts_adv_favorite" label="Destacado" value={advanced.favorite}
                    onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))}
                    options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Sólo destacados" }, { value: "NO", label: "No destacados" }]} />
                <FieldInput id="contacts_adv_org" label="Organización / cargo" value={advanced.organization}
                    onChange={(event) => setAdvanced((current) => ({ ...current, organization: event.target.value }))} placeholder="Empresa, área o cargo" />
                <FieldInput id="contacts_adv_tag" label="Etiqueta" value={advanced.tag}
                    onChange={(event) => setAdvanced((current) => ({ ...current, tag: event.target.value }))} placeholder="Etiqueta" />
                <FieldSelect id="contacts_adv_date_field" label="Fecha" value={advanced.dateField}
                    onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))}
                    options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Fecha de creación" }, { value: "BIRTHDAY", label: "Cumpleaños" }]} />
                <FieldDate id="contacts_adv_from" label="Desde" value={advanced.dateRange?.[0] || null}
                    onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                <FieldDate id="contacts_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null}
                    onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                <FieldDate id="contacts_adv_exact" label="Fecha exacta" value={advanced.dateExact}
                    onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
            </AdvancedFilterPanel>}
            exportColumns={[
                { key: "group", label: "Grupo" }, { key: "displayName", label: "Contacto" }, { key: "type", label: "Tipo" },
                { key: "relationship", label: "Relación" }, { key: "phone", label: "Teléfono" }, { key: "email", label: "Correo" },
                { key: "organization", label: "Organización" }, { key: "jobTitle", label: "Cargo" }, { key: "birthday", label: "Cumpleaños" }, { key: "tags", label: "Etiquetas" },
            ]}
            exportRows={exportRows} exportName="contactos" /> : null}
    >
        <div className="LStudio__contacts_security_notice">
            <FaLock />
            <div><strong>Agenda cifrada</strong><span>Los datos personales y los nombres de grupos se almacenan con AES-256-GCM.</span></div>
        </div>

        <div className="LStudio__contacts_group_section">
            <div className="LStudio__contacts_group_heading">
                <div><strong>Grupos</strong></div>
                <small>{(hasSearch || hasAdvanced)
                    ? `${visibleGroupingBucketCount} ${visibleGroupingBucketCount === 1 ? "resultado" : "resultados"}`
                    : `${groupingBucketCount} ${groupingBucketCount === 1 ? "grupo" : "grupos"}`}</small>
            </div>
            {(hasSearch || hasAdvanced) && !visibleGroups.length && !showUngrouped && !matchingContacts.length ? <EmptyState size="lg" icon={<FaAddressBook />}
                title="No hay coincidencias"
                description="Cambiá la búsqueda o los filtros avanzados para volver a ver grupos y contactos."
                action={<AppButton variant="outline" tone="neutral" onClick={() => { setSearch(""); clearAdvanced(); }}>Limpiar filtros</AppButton>} /> : <div className="LStudio__contacts_group_grid">
                {showUngrouped && <button type="button" className="LStudio__contacts_group_card" data-ungrouped="true" onClick={() => openWorkspace("NONE", hasSearch ? useSearch : "")}>
                    <span className="LStudio__contacts_group_icon"><FaAddressBook /></span>
                    <span><strong>Sin grupo</strong><small>{hasSearch
                        ? `${matchingUngroupedCount} de ${displayUngroupedCount} ${displayUngroupedCount === 1 ? "contacto" : "contactos"}`
                        : `${displayUngroupedCount} ${displayUngroupedCount === 1 ? "contacto" : "contactos"}`}</small></span>
                </button>}

                {(!(hasSearch || hasAdvanced) || matchingContacts.length > 0) && <button type="button" className="LStudio__contacts_group_card" onClick={() => openWorkspace("ALL", hasSearch ? useSearch : "")}>
                    <span className="LStudio__contacts_group_icon"><FaUsers /></span>
                    <span><strong>Todos</strong><small>{hasSearch
                        ? `${matchingContacts.length} de ${advancedContacts.length} ${advancedContacts.length === 1 ? "contacto" : "contactos"}`
                        : `${advancedContacts.length} ${advancedContacts.length === 1 ? "contacto" : "contactos"}`}</small></span>
                </button>}

                {visibleGroups.map((group) => {
                    const groupRows = advancedContacts.filter((contact) => String(contact.groupId) === String(group.id));
                    const count = groupRows.length;
                    const matchingCount = hasSearch ? groupRows.filter((contact) => matchingContactIds.has(String(contact.id))).length : count;
                    const groupMatchesItself = hasSearch && onMatchesSearch(useSearch, groupSearchParts(group));
                    const searchForWorkspace = hasSearch && !groupMatchesItself ? useSearch : "";
                    return <div key={group.id} className="LStudio__contacts_group_card" data-color={group.color || "GRAPHITE"}>
                        <button type="button" className="LStudio__contacts_group_main" onClick={() => openWorkspace(group.id, searchForWorkspace)}>
                            <span className="LStudio__contacts_group_icon">{groupIcon(group.icon)}</span>
                            <span><strong>{group.isFavorite && <FaStar title="Destacado" />} {group.name}</strong><small>{hasSearch && !groupMatchesItself
                                ? `${matchingCount} de ${count} ${count === 1 ? "contacto" : "contactos"}`
                                : `${count} ${count === 1 ? "contacto" : "contactos"}`}</small></span>
                        </button>
                        <CardActionMenu title="Acciones del grupo" items={[
                            { key: "open", icon: <FaAddressBook />, label: "Abrir grupo", onClick: () => openWorkspace(group.id, searchForWorkspace) },
                            { key: "new-contact", icon: <FaPlus />, label: "Nuevo contacto en este grupo", onClick: () => openCreate(group.id) },
                            { key: "edit", icon: <FaPen />, label: "Editar grupo", onClick: () => openGroupEdit(group) },
                            { divider: true },
                            { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchiveGroup(group) },
                        ]} />
                    </div>;
                })}

            </div>}
        </div>

        <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
            title="Estadísticas · Contactos" description="Resumen general de tu agenda privada."
            metrics={[
                { key: "groups", label: "Grupos", value: groupingBucketCount, icon: <FaLayerGroup />, tone: "neutral", isLoading: loading },
                { key: "contacts", label: "Contactos", value: rows.length, icon: <FaAddressBook />, tone: "accent", isLoading: loading },
                { key: "favorite", label: "Destacados", value: favoriteCount, icon: <FaStar />, tone: "neutral", isLoading: loading },
                { key: "whatsapp", label: "Con WhatsApp", value: whatsappCount, icon: <FaWhatsapp />, tone: "ok", isLoading: loading },
                { key: "birthday", label: "Con cumpleaños", value: birthdayCount, icon: <FaBirthdayCake />, tone: "neutral", isLoading: loading },
                { key: "ungrouped", label: "Sin grupo", value: ungroupedCount, icon: <FaUsers />, tone: "neutral", isLoading: loading },
            ]} />

        <ContactGroupWorkspaceModal
            isOpen={workspaceScope !== null}
            scope={workspaceScope || "ALL"}
            group={workspaceGroup}
            groups={groups}
            contacts={advancedContacts}
            initialSearch={workspaceSearch}
            onClose={closeWorkspace}
            onNewContact={(group) => openCreate(group?.id || "")}
            onEditContact={openEdit}
            onEditGroup={openGroupEdit}
            onToggleFavorite={onToggleFavorite}
            onArchiveContact={onArchive}
            isLoading={loading}
        />

        <ContactFormModal isOpen={useFormOpen} contact={useEditing} groups={groups} contacts={rows}
            defaultGroupId={defaultGroupId}
            onClose={() => { dialogClosingRef.current = true; setFormOpen(false); setEditing(null); setDefaultGroupId(""); setDialogRoute(null); }}
            onSaved={(saved, isEdit) => {
                CONTACT_QUERY.mutateLocal(isEdit ? { id: saved.id, patch: saved } : { insert: saved });
                dialogClosingRef.current = true;
                setDialogRoute(null);
                setEditing(null);
                setDefaultGroupId("");
            }} />

        <ContactGroupModal isOpen={groupModalOpen} group={editingGroup} groups={groups}
            onClose={() => { dialogClosingRef.current = true; setGroupModalOpen(false); setEditingGroup(null); setDialogRoute(null); }}
            onSaved={(saved, isEdit) => {
                GROUP_QUERY.mutateLocal(isEdit ? { id: saved.id, patch: saved } : { insert: saved });
                dialogClosingRef.current = true;
                setDialogRoute(null);
                setEditingGroup(null);
                if (!isEdit) openWorkspace(String(saved.id));
            }} />
    </ViewLayout>;
};
